import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { ScrollbarWrapper } from "../../../../../../components/ScrollbarWrapper";
import Scrollbar from "smooth-scrollbar";

const SelectPlan = ({ name, label, selectedValue, disabled, required, error, options = [], onChange, searchInDropdown = true, searchVisible = true }) => {
    const [selectDown, isSelectDown] = useState(false);
    const [selectedOption, setSelectedOption] = useState(selectedValue);
    const [selectedOptionName, setSelectedOptionName] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const selectRef = useRef(null);
    const scrollbarRef = useRef(null);
    const [scrollActive, setScrollActive] = useState(true);
    const [hasVisibleTrack, setHasVisibleTrack] = useState(false);
    const [resetValue, setResetValue] = useState("");

    const selectToggle = () => {
        isSelectDown(!selectDown);
    };

    const handleOptionChange = (option) => {
        const comparedOption = options.find((o) => o.id === option);
        if (comparedOption) {
            setSelectedOptionName(comparedOption.classNumber + " | " + comparedOption.languageName + " " + comparedOption.languageProgramName);
            setSelectedOption(comparedOption.id);
        }
        onChange({ target: { name, value: comparedOption.description || "" } });
        isSelectDown(false);
        setResetValue("");
        setSearchTerm("");
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setResetValue(event.target.value);
    };

    const handleClickOutside = (event) => {
        if (selectRef.current && !selectRef.current.contains(event.target)) {
            isSelectDown(false);
        }
    };

    const handleResize = () => {
        setWindowWidth(window.innerWidth);
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        window.addEventListener("resize", handleResize);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        const comparedOption = options.find((o) => o.id === selectedValue);
        if (comparedOption) {
            setSelectedOptionName(comparedOption.classNumber + " | " + comparedOption.languageName + " " + comparedOption.languageProgramName);
            setSelectedOption(comparedOption.id);
        }
    }, [selectedValue, options]);

    useLayoutEffect(() => {
        if (scrollbarRef?.current) {
            Scrollbar.init(scrollbarRef?.current, {
                damping: 0.1,
                thumbMinSize: 20,
                renderByPixels: true,
                continuousScrolling: "auto",
                alwaysShowTracks: true,
                delegateTo: null,
                plugins: {},
            });

            function setupScrollbar(options = {}) {
                const scrollbar = Scrollbar.get(scrollbarRef?.current);
                if (!scrollbar) return;

                if (options.removeXTrack && scrollbar.track.xAxis.element) {
                    scrollbar.track.xAxis.element.remove();
                }
                if (options.removeYTrack && scrollbar.track.yAxis.element) {
                    scrollbar.track.yAxis.element.remove();
                }

                let isScrolling;
                scrollbar.addListener(() => {
                    setScrollActive(true);

                    clearTimeout(isScrolling);
                    isScrolling = setTimeout(() => {
                        setScrollActive(false);
                    }, 200);
                });

                const toggleScrollbarClass = () => {
                    const tracks = scrollbarRef?.current?.querySelectorAll(`#mainContentScrollbar > .scrollbar-track`);

                    const hasVisibleTrack = tracks?.length > 0 && Array.from(tracks).some((track) => getComputedStyle(track).display === "block");

                    if (hasVisibleTrack) {
                        setHasVisibleTrack(true);
                    } else {
                        setHasVisibleTrack(false);
                    }
                };

                const tracks = scrollbarRef?.current?.querySelectorAll(`#mainContentScrollbar > .scrollbar-track`);
                const observer = new MutationObserver(() => {
                    toggleScrollbarClass();
                });

                tracks.forEach((track) => {
                    observer.observe(track, { attributes: true, attributeFilter: ["style"] });
                });

                if (!!options?.observeResize) {
                    const resizeTarget = document.querySelector(options.observeResize);
                    if (resizeTarget) {
                        const resizeObserver = new ResizeObserver(() => {
                            setTimeout(() => {
                                scrollbar.update();
                            }, 1000);
                            toggleScrollbarClass();
                        });
                        resizeObserver.observe(resizeTarget);
                    }
                }
            }

            setupScrollbar(options);
        }
    }, []);
    return (
        <>
            {/* <label htmlFor={`input-${name}`} className={`${required ? "input-required" : ""}`}>
                {label}
            </label> */}
            <div className="dropdown dropdown--select" style={{ width: "100%" }} ref={selectRef}>
                {searchInDropdown ? (
                    <div className="dropdown__toggle" onClick={selectToggle}>
                        {selectedOptionName || "Select Plan"}
                    </div>
                ) : (
                    <input
                        type="text"
                        className="dropdown__menu__search--input"
                        placeholder={selectedOptionName || ""}
                        onChange={handleSearchChange}
                        onClick={selectToggle}
                        value={resetValue}
                    />
                )}
                <div className={`dropdown__menu ${selectDown ? "active" : ""}`}>
                    {searchInDropdown && searchVisible ? (
                        <input type="text" className="dropdown__menu__search" placeholder="Search option" onChange={handleSearchChange} />
                    ) : (
                        <input
                            type="text"
                            className="dropdown__menu__search--input"
                            placeholder={selectedOptionName || "Select plan"}
                            onChange={handleSearchChange}
                            onClick={selectToggle}
                            value={resetValue}
                        />
                    )}
                    <div
                        ref={scrollbarRef}
                        id="mainContentScrollbar"
                        className={`dropdown__menu__wrap ${scrollActive ? "active-scroll" : ""} ${hasVisibleTrack ? "scrollbar-on" : ""}`}
                        data-scrollbar
                    >
                        <div className="scroll-content">
                            {options.map((option, index) => (
                                <div
                                    className="dropdown__menu__item"
                                    key={`${option?.id}-${index}`}
                                    data-value={option?.language}
                                    onClick={() => handleOptionChange(option?.id)}
                                    style={{ 
                                        display: (
                                            option.classNumber.toString().toLowerCase() +
                                            " " +
                                            option.languageName.toLowerCase() +
                                            " " +
                                            option.languageProgramName.toLowerCase()
                                          ).includes(searchTerm.toLowerCase())
                                            ? ""
                                            : "none"
                                    }}
                                >
                                    <span>
                                        {option?.classNumber} | {option?.languageName} - {option?.languageProgramName}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {error && <div className="error-message-text">{error}</div>}
        </>
    );
};

export default SelectPlan;
